// @ts-check
// Modo HTML (spec/email.md seção 19.2): importar (colar ou enviar um arquivo .html de até 500 KB), o relatório do que
// saiu por segurança e os avisos (Outlook, imagens http, rodapé acrescentado), e a tela dividida com o código e a
// prévia, atualizada 800 ms depois da última tecla pelo saneador e pelo renderizador no navegador. A prévia é sempre
// um iframe sandbox="" (RISCO-23). No celular, abas "Código" e "Prévia".
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { MAXIMUM_IMPORT_INPUT_BYTES, sanitizeEmailHtml } from '../../../../shared/email-design/sanitize-email-html.js';
import { importEmailHtml } from '../../data/email-templates.js';
import { isMockEnabled } from '../../data/api-client.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { TabList } from '../../ui/tab-list.js';
import { useIsMobile } from '../../ui/hooks.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { SandboxedHtmlFrame } from './sandboxed-html-frame.js';
import { VariablePicker } from './variable-picker.js';
import { sanitizeReportSentence } from './sanitize-report.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/email-design/email-document.js').EmailDocument} EmailDocument
 * @typedef {import('../../../../shared/email-design/sanitize-email-html.js').SanitizeReport} SanitizeReport
 */

const PREVIEW_DELAY_MILLISECONDS = 800;
const EMPTY_SOURCE = /^<html[^>]*>\s*<head>\s*<\/head>\s*<body[^>]*>\s*<\/body>\s*<\/html>$/i;
const UNSUBSCRIBE_LINK = /href="\{\{\s*unsubscribeUrl\s*(?:\|[^"{}|]{0,60})?\}\}"/;

/** Documento HTML ainda sem conteúdo (recém-criado em branco). @param {EmailDocument} emailDocument */
export const isEmptyHtmlDocument = (emailDocument) => EMPTY_SOURCE.test((emailDocument.html?.source ?? '').trim());

/** @param {string} source */
const sanitizeSource = (source) => sanitizeEmailHtml(source, { profile: 'IMPORTED_DOCUMENT', allowDataImages: isMockEnabled() });

/**
 * Importação: colar o código ou escolher o arquivo.
 * @param {{ onImported: (result: { document: EmailDocument, warnings: Array<{ code: string }> }) => void, onCancel?: () => void }} props
 */
export function HtmlImportPanel({ onImported, onCancel }) {
  const [pasted, setPasted] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));

  /** @param {{ html: string } | { file: File }} input */
  const runImport = async (input) => {
    setError('');
    const size = 'file' in input ? input.file.size : new TextEncoder().encode(input.html).length;
    if (size > MAXIMUM_IMPORT_INPUT_BYTES) {
      setError(t('errors.HTML_TOO_LARGE'));
      return;
    }
    setIsImporting(true);
    try {
      const result = await importEmailHtml(input);
      onImported({ document: result.document, warnings: result.warnings ?? [] });
    } catch (importError) {
      showErrorSnackbar(importError);
    } finally {
      setIsImporting(false);
    }
  };

  return html`<div class="email-html-import">
    <label class="input-label" for="email-html-paste">${t('emailDesign.html.paste')}</label>
    <textarea id="email-html-paste" class="text-input email-textarea email-code-input" rows="12" spellcheck="false" value=${pasted}
      placeholder=${'<html>...</html>'} onInput=${(/** @type {Event} */ event) => setPasted(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
    <p class="input-hint">${t('emailDesign.html.importHint')}</p>
    ${error ? html`<p class="input-hint" data-danger="true" role="alert">${error}</p>` : null}
    <div class="email-button-row">
      <${Button} title=${t('emailDesign.html.import')} icon="IconFileImport" size="small" accent="blue" disabled=${!pasted.trim()} isLoading=${isImporting}
        onClick=${() => runImport({ html: pasted })} />
      <${Button} title=${t('emailDesign.html.upload')} icon="IconUpload" size="small" variant="secondary" disabled=${isImporting} onClick=${() => fileInputRef.current?.click()} />
      ${onCancel ? html`<${Button} title=${t('common.cancel')} size="small" variant="tertiary" onClick=${onCancel} />` : null}
      <input ref=${fileInputRef} type="file" accept=".html,.htm,text/html" hidden
        onChange=${(/** @type {Event} */ event) => {
          const input = /** @type {HTMLInputElement} */ (event.currentTarget);
          const file = input.files?.[0];
          input.value = '';
          if (file) runImport({ file });
        }} />
    </div>
  </div>`;
}

/**
 * Relatório do saneamento e avisos da importação.
 * @param {{ report: SanitizeReport | null | undefined, warnings: string[] }} props
 */
export function HtmlReport({ report, warnings }) {
  const sentence = sanitizeReportSentence(report);
  if (!sentence && warnings.length === 0) return null;
  return html`<div class="email-html-report" role="status">
    ${sentence ? html`<p><${Icon} name="IconShieldCheck" size=${14} />${sentence}</p>` : null}
    ${warnings.map((code) => html`<p data-severity="warning"><${Icon} name="IconInfoCircle" size=${14} />${t(`emailTemplates.problems.${code}`)}</p>`)}
  </div>`;
}

/**
 * @param {{ document: EmailDocument, onChange: (document: EmailDocument, options?: { coalesceKey?: string }) => void, readOnly?: boolean,
 *   previewHtml: (document: EmailDocument) => string, catalog: import('../../data/email-templates.js').EmailVariableCatalog | null,
 *   estimatedHeight?: number }} props
 */
export function HtmlMode({ document: emailDocument, onChange, readOnly = false, previewHtml, catalog, estimatedHeight = 900 }) {
  const isMobile = useIsMobile();
  const source = emailDocument.html?.source ?? '';
  const [draft, setDraft] = useState(source);
  const [isImporting, setIsImporting] = useState(() => !readOnly && isEmptyHtmlDocument(emailDocument));
  const [confirmReimport, setConfirmReimport] = useState(false);
  const [importWarnings, setImportWarnings] = useState(/** @type {string[]} */ ([]));
  const [mobileTab, setMobileTab] = useState(/** @type {'code' | 'preview'} */ ('code'));
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(emailDocument);
  const textareaRef = useRef(/** @type {HTMLTextAreaElement | null} */ (null));
  const gutterRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const variableAnchorRef = useRef(/** @type {HTMLElement | null} */ (null));
  const timerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const lastSourceRef = useRef(source);
  const documentRef = useRef(emailDocument);
  documentRef.current = emailDocument;

  // valor de fora (desfazer, conversão): o código mostra o documento
  useEffect(() => {
    if (source === lastSourceRef.current) return;
    lastSourceRef.current = source;
    setDraft(source);
    setPreviewDocument(emailDocument);
  }, [source]);
  useEffect(() => setPreviewDocument(emailDocument), [emailDocument.style]);
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  /** Saneia o rascunho e grava no documento (o código continua como foi digitado até sair do campo). @param {string} code */
  const commit = (code) => {
    const result = sanitizeSource(code);
    if (result.problems.some((problem) => problem.code === 'HTML_TOO_LARGE')) return null;
    const current = documentRef.current;
    const next = {
      ...current,
      html: { source: result.html, importedAt: current.html?.importedAt ?? new Date().toISOString(), report: result.report },
    };
    lastSourceRef.current = result.html;
    setPreviewDocument(next);
    if (result.html !== current.html?.source) onChange(next, { coalesceKey: 'htmlSource' });
    return result.html;
  };
  /** @param {string} code */
  const changeDraft = (code) => {
    setDraft(code);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => commit(code), PREVIEW_DELAY_MILLISECONDS);
  };
  /** @param {string} expression */
  const insertVariable = (expression) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? draft.length;
    const end = textarea?.selectionEnd ?? draft.length;
    changeDraft(`${draft.slice(0, start)}${expression}${draft.slice(end)}`);
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + expression.length, start + expression.length);
    });
  };

  /** @param {{ document: EmailDocument, warnings: Array<{ code: string }> }} result */
  const applyImport = (result) => {
    const current = documentRef.current;
    const next = { ...result.document, style: { ...current.style, ...result.document.style } };
    lastSourceRef.current = next.html?.source ?? '';
    setDraft(lastSourceRef.current);
    setPreviewDocument(next);
    setImportWarnings(result.warnings.map((warning) => warning.code));
    setIsImporting(false);
    onChange(next);
  };

  // a prévia só muda quando o rascunho é saneado (800 ms depois da última tecla), não a cada tecla
  const previewSource = useMemo(() => previewHtml(previewDocument), [previewDocument]);

  if (isImporting) {
    return html`<div class="email-html-mode" data-state="import">
      <${HtmlImportPanel} onImported=${applyImport} onCancel=${isEmptyHtmlDocument(emailDocument) ? undefined : () => setIsImporting(false)} />
    </div>`;
  }

  const footerAdded = !UNSUBSCRIBE_LINK.test(draft);
  const warnings = [...new Set([...importWarnings.filter((code) => code !== 'FOOTER_ADDED'), ...(footerAdded ? ['FOOTER_ADDED'] : [])])];
  const lineCount = Math.max(1, draft.split('\n').length);
  const preview = html`<div class="email-html-preview">
    <${SandboxedHtmlFrame} html=${previewSource} title=${t('emailDesign.html.preview')} height=${Math.min(2400, Math.max(480, estimatedHeight + 160))} />
  </div>`;
  const code = html`<div class="email-html-code">
    <div class="email-code-editor">
      <div ref=${gutterRef} class="email-code-gutter" aria-hidden="true">${Array.from({ length: Math.min(lineCount, 20000) }, (_, index) => html`<span>${index + 1}</span>`)}</div>
      <textarea
        ref=${textareaRef}
        class="email-code-textarea"
        spellcheck="false"
        wrap="off"
        aria-label=${t('emailDesign.html.code')}
        readOnly=${readOnly}
        value=${draft}
        onInput=${(/** @type {Event} */ event) => changeDraft(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}
        onScroll=${(/** @type {Event} */ event) => {
          if (gutterRef.current) gutterRef.current.scrollTop = /** @type {HTMLTextAreaElement} */ (event.currentTarget).scrollTop;
        }}
        onBlur=${() => {
          if (readOnly) return;
          if (timerRef.current) clearTimeout(timerRef.current);
          const sanitized = commit(draft);
          if (sanitized !== null) setDraft(sanitized);
        }}
      ></textarea>
    </div>
  </div>`;

  return html`<div class="email-html-mode" data-layout=${isMobile ? 'mobile' : 'split'}>
    <div class="email-html-toolbar">
      ${readOnly
        ? null
        : html`
            <span ref=${variableAnchorRef}><${Button} title=${t('emailDesign.html.insertVariable')} icon="IconBraces" size="small" variant="secondary"
              onClick=${() => setIsPickerOpen((open) => !open)} /></span>
            <${Button} title=${t('emailDesign.html.reimport')} icon="IconFileImport" size="small" variant="tertiary" onClick=${() => setConfirmReimport(true)} />
          `}
      <span class="email-html-size">${t('emailDesign.html.size', { size: Math.ceil(new TextEncoder().encode(draft).length / 1024) })}</span>
    </div>
    <${HtmlReport} report=${emailDocument.html?.report} warnings=${warnings} />
    ${isMobile
      ? html`<${TabList} tabs=${[{ id: 'code', title: t('emailDesign.html.code') }, { id: 'preview', title: t('emailDesign.html.preview') }]}
            activeTabId=${mobileTab} onChange=${(/** @type {any} */ tab) => setMobileTab(tab)} />
          ${mobileTab === 'code' ? code : preview}`
      : html`<div class="email-html-split">${code}${preview}</div>`}
    <${VariablePicker} anchorRef=${variableAnchorRef} isOpen=${isPickerOpen} onClose=${() => setIsPickerOpen(false)} catalog=${catalog}
      onSelect=${(/** @type {string} */ expression) => insertVariable(expression)} />
    ${confirmReimport
      ? html`<${Modal} title=${t('emailDesign.html.reimportTitle')} subtitle=${t('emailDesign.html.reimportDescription')} confirmLabel=${t('emailDesign.html.reimport')}
          onClose=${() => setConfirmReimport(false)} onConfirm=${() => { setConfirmReimport(false); setIsImporting(true); }} />`
      : null}
  </div>`;
}
